import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  NgModule,
  OnInit,
} from '@angular/core';
import { FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TooltipModule } from '@commons/tooltip/tooltip.component';
import { QuestionBaseDirective } from '@domains/companies/register-form/models/component/question.base.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { distinctUntilChanged } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-form-text',
  templateUrl: './text.component.html',
  styleUrls: ['./text.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextComponent),
      multi: true,
    },
    { provide: NG_VALIDATORS, multi: true, useExisting: forwardRef(() => TextComponent) },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextComponent extends QuestionBaseDirective implements OnInit {
  get elm_id(): string {
    return this.id ?? this._id;
  }

  set value(value: string) {
    if (this._value === value) {
      return;
    }
    this._value = value;
    this.control.setValue(value);
    this.onChanged(value);
    this.onTouched();
  }
  get value(): string {
    return this._value;
  }

  set disabled(isDisabled: boolean) {
    this._disabled = isDisabled;
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }
  get disabled(): boolean {
    return this._disabled;
  }

  get title(): string {
    return this.question?.title ?? '';
  }

  get subtitle(): string {
    return this.question?.subtitle ?? '';
  }

  get tooltip(): string {
    return this.question?.tooltip ?? '';
  }

  // VALUES
  private static Counter = 0;
  @Input() btnLabel?: string;

  private _value!: string;

  private _disabled!: boolean;

  private readonly _id = `wzb-text-${++TextComponent.Counter}`;

  control!: FormControl;

  constructor(private readonly cdr: ChangeDetectorRef) {
    super();
  }

  onChanged: (_: string) => void = (_: string) => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  ngOnInit(): void {
    this.control = this.initFormControl(this.question);

    this.control.valueChanges.pipe(untilDestroyed(this), distinctUntilChanged()).subscribe({
      next: (value) => {
        this.value = value;
      },
    });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChanged = (value) => {
      fn(value);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  setDisabledState = (isDisabled: boolean): void => {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  };

  writeValue = (value: string): string => (this.value = value);

  next(control: FormControl): void {
    this.control.markAllAsTouched();
    this.control.updateValueAndValidity();
    this.nextQuestion.emit(control);
  }
}

/**
 * Module exporting the TextComponent and its dependencies
 */
@NgModule({
  imports: [CommonModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule, TooltipModule],
  declarations: [TextComponent],
  exports: [TextComponent],
})
export class TextModule {}
